import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight, PartyPopper, Share2 } from "lucide-react";
import api from "../lib/api";

const MILESTONES = [100, 75, 50];
const DISMISS_KEY = "reddog_milestone_dismissed_v1";
const DISMISS_HOURS = 24;

const pickMilestone = (campaign) => {
    const pct = campaign.percent || 0;
    for (const m of MILESTONES) {
        if (pct >= m) return m;
    }
    return null;
};

const milestoneCopy = (campaign, ms) => {
    const remaining = Math.max(campaign.goal_amount - campaign.raised, 0);
    const remainingFmt = `$${Math.round(remaining).toLocaleString()}`;
    if (ms === 100) {
        return {
            tag: "Goal reached",
            headline: `${campaign.name} hit 100% — thank you!`,
            sub: `Together we raised $${Math.round(campaign.raised).toLocaleString()}. Every gift made it possible.`,
        };
    }
    if (ms === 75) {
        return {
            tag: "75% milestone",
            headline: `${campaign.name} is 75% there!`,
            sub: `Only ${remainingFmt} to go before we hit the goal — push us over the finish line.`,
        };
    }
    return {
        tag: "Halfway there",
        headline: `${campaign.name} just hit 50%!`,
        sub: `We've raised $${Math.round(campaign.raised).toLocaleString()} of $${Math.round(campaign.goal_amount).toLocaleString()}. Help us double it.`,
    };
};

const MilestoneBanner = () => {
    const [campaign, setCampaign] = useState(null);
    const [ms, setMs] = useState(null);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                const age = (Date.now() - parsed.at) / 1000 / 3600;
                if (age < DISMISS_HOURS) {
                    setHidden(true);
                    return;
                }
            }
        } catch {}

        api.get("/campaigns/public")
            .then((r) => {
                const sorted = [...r.data].sort((a, b) => (b.percent || 0) - (a.percent || 0));
                for (const c of sorted) {
                    const m = pickMilestone(c);
                    if (m) {
                        setCampaign(c);
                        setMs(m);
                        return;
                    }
                }
            })
            .catch(() => {});
    }, []);

    if (hidden || !campaign || ms === null) return null;

    const copy = milestoneCopy(campaign, ms);
    const isComplete = ms === 100;

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, JSON.stringify({ at: Date.now(), id: campaign.id, ms }));
        } catch {}
        setHidden(true);
    };

    const handleShare = () => {
        const shareUrl = `${window.location.origin}/donate?campaign=${campaign.id}`;
        const text = `${copy.headline} ${copy.sub}`;
        if (navigator.share) {
            navigator.share({ title: copy.headline, text, url: shareUrl }).catch(() => {});
        } else {
            window.open(
                `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`,
                "_blank",
                "width=600,height=500"
            );
        }
    };

    return (
        <div
            data-testid="milestone-banner"
            role="region"
            aria-label="Campaign milestone announcement"
            className="relative overflow-hidden bg-[#1C252E] text-white lg:pl-20"
        >
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#E53935]/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-56 h-56 bg-[#E53935]/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-4 sm:py-5 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-[#E53935] flex items-center justify-center">
                        {isComplete ? (
                            <PartyPopper className="w-5 h-5 text-white" strokeWidth={1.5} />
                        ) : (
                            <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
                        )}
                    </div>
                    <span className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935]" data-testid="milestone-tag">
                        {copy.tag}
                    </span>
                </div>
                <div className="flex-1 min-w-[250px]">
                    <p className="font-heading text-base sm:text-lg text-white font-medium leading-tight" data-testid="milestone-headline">
                        {copy.headline}
                    </p>
                    <p className="text-xs sm:text-sm text-white/70 mt-0.5" data-testid="milestone-sub">{copy.sub}</p>
                </div>
                <Link
                    to={`/donate?campaign=${campaign.id}`}
                    data-testid="milestone-cta"
                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 whitespace-nowrap"
                >
                    {isComplete ? "Keep the momentum" : "Push it over"}
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
                <button
                    onClick={handleShare}
                    data-testid="milestone-share-btn"
                    title="Share this milestone"
                    className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    aria-label="Share"
                >
                    <Share2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                    onClick={dismiss}
                    data-testid="milestone-dismiss"
                    className="p-2 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    aria-label="Dismiss"
                >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
            </div>
        </div>
    );
};

export default MilestoneBanner;
